import Environments, { APIs } from '@delta-defense/client-environments';
import { APIData, ApiResult } from '@delta-defense/delta-development-kit/apiResult';
import { IRest, Rest } from '../rest/rest';

type ResumeRegistrationData = APIData<{
	eventId: number;
	token: string;
	url: string;
}>;
type EventData = APIData<{
	name: string;
	description: string;
	type: string;
	slug: string;
	status: string;
	startDate: string;
	endDate: string;
	closeDate: string;
	timezone: string;
	locationId: string;
	businessId: string;
	templateId: string;
	externalId: string;
	capacity: number;
	price: number;
	isSearchable: boolean;
}>;

export interface IEvents {
	getResumeUrl(eventId: string, accessToken: string): Promise<ApiResult<ResumeRegistrationData>>;
	getEventData(eventId: string): Promise<ApiResult<EventData>>;
}

export class Events implements IEvents {
	private static instance: IEvents | null = null;
	public static Instance(
		eventsProxyDomain: string = Environments.getProxyEndpointForApi(APIs.Events),
		rest = Rest.Instance()
	): IEvents {
		return this.instance || (this.instance = new Events(eventsProxyDomain, rest));
	}
	public static Destroy = () => (Events.instance = null);

	private constructor(
		private eventsProxyDomain: string,
		public rest: IRest
	) {}
	private resumeUrlCache: Record<string, ApiResult<ResumeRegistrationData>> = {};

	public getResumeUrl = async (eventId: string, accessToken: string) => {
		const cacheKey = `${eventId}|${accessToken}`;
		if (this.resumeUrlCache[cacheKey]) {
			return this.resumeUrlCache[cacheKey];
		}

		const response = await this.rest.Get<ResumeRegistrationData>(
			`${this.eventsProxyDomain}/events/${eventId}/registration/resume`,
			{ 'X-Auth-Token': accessToken }
		);
		const result = {
			wasSuccessful: response.ok,
			value: response.body,
			status: response.status,
			statusText: response.statusText
		};

		if (result.wasSuccessful) {
			this.resumeUrlCache[cacheKey] = result;
		}

		return result;
	};

	private eventDataCache: Record<string, ApiResult<EventData>> = {};
	public async getEventData(eventId: string): Promise<ApiResult<EventData>> {
		if (this.eventDataCache[eventId]) {
			return this.eventDataCache[eventId];
		}

		const response = await this.rest.Get<EventData>(`${this.eventsProxyDomain}/events/${eventId}`);
		const result = {
			wasSuccessful: response.ok,
			value: response.body,
			status: response.status,
			statusText: response.statusText
		};

		if (result.wasSuccessful) {
			this.eventDataCache[eventId] = result;
		}

		return result;
	}
}
